import {
  ActionSheet,
  ActionSheetContent,
  ActionSheetItem,
  ActionSheetTrigger,
} from '@mdvip/shared/components/blocks/ActionSheet';
import { Icon } from '@mdvip/shared/components/blocks/Icon';
import { Alert, AlertDescription, AlertTitle } from '@mdvip/shared/components/ui/alert';
import { Badge } from '@mdvip/shared/components/ui/badge';
import { Button } from '@mdvip/shared/components/ui/button';
import { Skeleton } from '@mdvip/shared/components/ui/skeleton';
import { TableCell, TableHead, TableHeader, TableRow } from '@mdvip/shared/components/ui/table';
import { useCurrency } from '@mdvip/shared/hooks/useCurrency';
import { useErrorTranslation } from '@mdvip/shared/hooks/useErrorTranslation';
import { useFormatDate } from '@mdvip/shared/hooks/useFormatDate';
import { extractGqlErrorCode } from '@mdvip/shared/lib/gqlErrors';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PaymentsTableShell } from '@/components/payments/PaymentsTableShell';
import type { UpcomingPayment } from '@/hooks/payments/upcomingPayments/upcomingPaymentsApi';
import { useUpcomingPayments } from '@/hooks/payments/upcomingPayments/useUpcomingPayments';
import { isScheduledToday } from '@/lib/payments/upcomingPayment.format';
import { AccountCell } from './AccountCell';
import { CancelPaymentDialog } from './CancelPaymentDialog';

const SKELETON_ROW_COUNT = 2;

/** How long the success confirmation stays up before clearing itself. */
const SUCCESS_BANNER_TIMEOUT_MS = 5000;

interface UpcomingPaymentsSectionProps {
  /** Opens the one-time payment modal from the header rail's Make payment button. */
  onMakePayment: () => void;
}

function RowActions({
  payment,
  amountLabel,
  dateLabel,
  onCancel,
}: {
  payment: UpcomingPayment;
  amountLabel: string;
  dateLabel: string;
  onCancel: (payment: UpcomingPayment) => void;
}) {
  const { t } = useTranslation('members');
  const disabled = isScheduledToday(payment.scheduledDate);
  const label = t('payments.upcomingPayments.rowActionsLabel', { amount: amountLabel, date: dateLabel });

  return (
    <ActionSheet>
      <ActionSheetTrigger
        render={
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={label}
            disabled={disabled}
            className="rounded-full text-muted-foreground"
          />
        }
      >
        <Icon name="ellipsisVertical" size="sm" />
      </ActionSheetTrigger>
      <ActionSheetContent title={label}>
        <ActionSheetItem icon={<Icon name="x" size="sm" />} destructive onSelect={() => onCancel(payment)}>
          {t('payments.upcomingPayments.cancelAction')}
        </ActionSheetItem>
      </ActionSheetContent>
    </ActionSheet>
  );
}

function SkeletonRows() {
  return (
    <>
      {Array.from({ length: SKELETON_ROW_COUNT }).map((_, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: fixed-count skeleton placeholders, never reordered
        <TableRow key={index}>
          <TableCell>
            <Skeleton className="h-3.5 w-20" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-3.5 w-16" />
          </TableCell>
          <TableCell>
            <div className="flex items-center gap-2.5">
              <Skeleton className="h-8 w-12 shrink-0 rounded-md" />
              <Skeleton className="h-3.5 w-28" />
            </div>
          </TableCell>
          <TableCell>
            <Skeleton className="h-5 w-20 rounded-full" />
          </TableCell>
          <TableCell />
        </TableRow>
      ))}
    </>
  );
}

/**
 * The Payment Center's "Upcoming payments" table — a read-only list of the member's scheduled
 * future one-time payments. Owns its own query; its only prop is the Make payment callback.
 * Renders through the shared `PaymentsTableShell` for the header rail and
 * error/loading/empty/loaded branching, same as Recent/Past Transactions. Make payment opens the
 * one-time payment modal, which the page owns so a second trigger can share the same instance.
 *
 * Owns the cancellation flow's local state: which row's dialog is open, and whether to show the
 * success banner. The banner goes in the shell's `notice` slot, which sits above the table in every
 * state, so it keeps the table's width per the design and still survives the flip to the empty
 * state when the last remaining row is cancelled.
 */
export function UpcomingPaymentsSection({ onMakePayment }: UpcomingPaymentsSectionProps) {
  const { t } = useTranslation('members');
  const tError = useErrorTranslation();
  const { data, isLoading, isError, error, refetch } = useUpcomingPayments();
  const { formatCurrency } = useCurrency();
  const { formatDateOnly } = useFormatDate();
  const [paymentToCancel, setPaymentToCancel] = useState<UpcomingPayment | null>(null);
  const [cancelSucceeded, setCancelSucceeded] = useState(false);

  // The banner has no dismiss control in the design, so it clears itself. A new cancellation
  // resets `cancelSucceeded` first, which re-runs this and restarts the countdown.
  useEffect(() => {
    if (!cancelSucceeded) return;
    const timer = setTimeout(() => setCancelSucceeded(false), SUCCESS_BANNER_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [cancelSucceeded]);

  const openCancelDialog = (payment: UpcomingPayment) => {
    // A fresh attempt supersedes the previous result, so the banner clears as the dialog opens.
    setCancelSucceeded(false);
    setPaymentToCancel(payment);
  };

  const isEmpty = (data?.length ?? 0) === 0;
  const state: 'error' | 'loading' | 'empty' | 'loaded' = isError
    ? 'error'
    : isLoading
      ? 'loading'
      : isEmpty
        ? 'empty'
        : 'loaded';

  return (
    <section className="mt-8 border-t pt-8" data-slot="upcoming-payments">
      <CancelPaymentDialog
        payment={paymentToCancel}
        onClose={() => setPaymentToCancel(null)}
        onSuccess={() => {
          setPaymentToCancel(null);
          setCancelSucceeded(true);
        }}
      />

      <PaymentsTableShell
        title={t('payments.upcomingPayments.title')}
        description={t('payments.upcomingPayments.disclaimer')}
        descriptionClassName="mt-4 text-xs"
        state={state}
        notice={
          cancelSucceeded && (
            <Alert variant="success" className="mb-4" data-slot="cancel-success-banner">
              <Icon name="circleCheck" weight="solid" />
              {/* The message sits in AlertTitle, per the design; Description renders muted. */}
              <AlertTitle>{t('payments.upcomingPayments.cancelSuccess')}</AlertTitle>
            </Alert>
          )
        }
        headerAction={
          <Button
            variant="outline"
            size="sm"
            onClick={onMakePayment}
            className="mt-4 w-fit lg:h-12 lg:px-6 lg:text-[16px] lg:leading-[24px]"
          >
            {t('payments.upcomingPayments.makePayment')}
          </Button>
        }
        errorContent={
          <Alert variant="destructive">
            <Icon name="alertTriangle" weight="solid" />
            <AlertTitle>{t('payments.upcomingPayments.errorTitle')}</AlertTitle>
            <AlertDescription>
              <p>{tError(extractGqlErrorCode(error))}</p>
              <Button variant="outline" size="sm" className="mt-3" onClick={() => refetch()}>
                <Icon name="refreshCw" size="sm" /> {t('payments.upcomingPayments.retry')}
              </Button>
            </AlertDescription>
          </Alert>
        }
        emptyState={
          <div className="flex flex-col items-center rounded-2xl border-2 border-dashed border-border-strong bg-muted px-6 py-12 text-center">
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
              <Icon name="calendarDays" size="lg" />
            </div>
            <p className="max-w-sm text-pretty text-sm text-muted-foreground">{t('payments.upcomingPayments.empty')}</p>
          </div>
        }
        tableHeader={
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="hidden sm:table-cell sm:w-40">
                {t('payments.upcomingPayments.columnDate')}
              </TableHead>
              <TableHead className="w-auto sm:w-28">{t('payments.upcomingPayments.columnAmount')}</TableHead>
              <TableHead className="sm:w-56">{t('payments.upcomingPayments.columnAccount')}</TableHead>
              <TableHead className="w-auto sm:w-36">{t('payments.upcomingPayments.columnType')}</TableHead>
              <TableHead className="text-right sm:w-14">
                <span className="sr-only">{t('payments.upcomingPayments.columnActions')}</span>
              </TableHead>
            </TableRow>
          </TableHeader>
        }
        skeleton={<SkeletonRows />}
        rows={(data ?? []).map((payment) => {
          // Salesforce stores a payment as a negative amount, since it debits the member's
          // balance. Product asked for the magnitude in the money columns, so a minus sign
          // doesn't read as an error. Render-time only; the API value is untouched.
          const amountLabel = formatCurrency(Math.abs(payment.amount));
          const dateLabel = formatDateOnly(payment.scheduledDate);
          return (
            <TableRow key={payment.id}>
              <TableCell className="hidden whitespace-nowrap text-sm tabular-nums text-muted-foreground sm:table-cell">
                {dateLabel}
              </TableCell>
              <TableCell className="w-auto align-top sm:w-28 sm:align-middle">
                <div className="whitespace-nowrap text-sm font-semibold tabular-nums text-foreground">
                  {amountLabel}
                </div>
                <div className="mt-1 text-xs tabular-nums text-muted-foreground sm:hidden">{dateLabel}</div>
              </TableCell>
              <TableCell className="align-top sm:w-56 sm:align-middle">
                <AccountCell account={payment} />
              </TableCell>
              <TableCell className="w-auto align-top sm:w-36 sm:align-middle">
                <Badge variant="muted">{t('payments.upcomingPayments.paymentType')}</Badge>
              </TableCell>
              <TableCell className="text-right align-top sm:align-middle">
                <div className="flex justify-end">
                  <RowActions
                    payment={payment}
                    amountLabel={amountLabel}
                    dateLabel={dateLabel}
                    onCancel={openCancelDialog}
                  />
                </div>
              </TableCell>
            </TableRow>
          );
        })}
      />
    </section>
  );
}
